"use client"

import React, { useRef, useState } from "react"
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { Card, CardContent } from "../ui/card"
import { Button } from "../ui/button"
import { Lock, Sparkles } from "lucide-react"
import { useSubscription } from "../../hooks/use-subscription"

const magneticButtonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:from-purple-700 hover:to-pink-700",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
      radius: {
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        full: "rounded-full",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "md",
    },
  }
)

export interface MagneticButtonProps 
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof magneticButtonVariants> {
  strength?: number
  range?: number
  springConfig?: {
    stiffness?: number
    damping?: number
  }
}

const MagneticButtonInternal = React.forwardRef<HTMLButtonElement, MagneticButtonProps>(
  ({ 
    children, 
    className,
    variant,
    size,
    radius, 
    strength = 0.3, 
    range = 100,
    springConfig = { stiffness: 200, damping: 15 },
    ...props 
  }, ref) => {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [isHovered, setIsHovered] = useState(false)

    const x = useMotionValue(0)
    const y = useMotionValue(0)

    const springX = useSpring(x, springConfig)
    const springY = useSpring(y, springConfig)

    const rotateX = useTransform(springY, [-range, range], [5, -5])
    const rotateY = useTransform(springX, [-range, range], [-5, 5])

    const handleMouseMove = (e: React.MouseEvent) => {
      if (!buttonRef.current) return

      const rect = buttonRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const deltaX = e.clientX - centerX
      const deltaY = e.clientY - centerY

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

      if (distance < range) {
        x.set(deltaX * strength)
        y.set(deltaY * strength)
      }
    }

    const handleMouseLeave = () => {
      setIsHovered(false)
      x.set(0)
      y.set(0)
    }

    const handleMouseEnter = () => {
      setIsHovered(true)
    }

    return (
      <motion.button
        ref={(node) => {
          buttonRef.current = node
          if (typeof ref === "function") {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
        }}
        className={cn(
          magneticButtonVariants({ variant, size, radius }),
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
        style={{
          x: springX,
          y: springY,
          rotateX,
          rotateY,
          transformPerspective: 1000,
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          rotateX: isHovered ? rotateX.get() : 0,
          rotateY: isHovered ? rotateY.get() : 0,
        }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="relative z-10 flex items-center justify-center"
          animate={{
            scale: isHovered ? 1.02 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>

        {/* Glow effect */}
        <motion.div
          className={cn(
            "absolute inset-0 bg-primary/20 blur-md",
            radius === "none" && "rounded-none",
            radius === "sm" && "rounded-sm",
            radius === "md" && "rounded-md",
            radius === "lg" && "rounded-lg",
            radius === "xl" && "rounded-xl",
            radius === "full" && "rounded-full",
            !radius && "rounded-md"
          )}
          animate={{
            opacity: isHovered ? 0.8 : 0,
            scale: isHovered ? 1.1 : 1,
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.button>
    )
  }
)

MagneticButtonInternal.displayName = "MagneticButtonInternal"

export const MagneticButton = React.forwardRef<HTMLButtonElement, MagneticButtonProps>(
  ({ className, ...props }, ref) => {
    // Check if we're in docs mode or have pro access
    const { hasProAccess, isLoading } = useSubscription()
    
    // In docs mode, always show the component
    
    // If not in docs mode and no pro access, show upgrade prompt
    if (!isLoading && !hasProAccess) {
      return (
        <Card className={cn("w-fit", className)}>
          <CardContent className="py-6 text-center">
            <div className="space-y-4">
              <div className="rounded-full bg-purple-100 dark:bg-purple-900/30 p-3 w-fit mx-auto">
                <Lock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-2">Pro Feature</h3>
                <p className="text-muted-foreground text-xs mb-4">
                  Magnetic Button is available exclusively to MoonUI Pro subscribers.
                </p>
                <a href="/pricing">
                  <Button size="sm">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return <MagneticButtonInternal className={className} ref={ref} {...props} />
  }
)

MagneticButton.displayName = "MagneticButton"